import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faClock,
  faMapMarkerAlt,
  faBrain,
  faCalendarAlt,
  faPlus,
  faArrowsRotate,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import TranscriptMessageBubble from "./TranscriptMessageBubble";
import {
  fetchVisitNotes,
  createVisitNote,
  fetchShiftVoiceCalls,
  regenerateShiftVoiceCallSummary,
} from "../utils/api";
import "./UnityCareVisit.css";

const CollapsibleSection = ({
  icon,
  title,
  color,
  children,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="visit-section">
      <div className="visit-section-header" onClick={onToggle}>
        <div className="vsh-left">
          <div className={`vsh-icon-circle ${color}`}>
            <FontAwesomeIcon icon={icon} />
          </div>
          <span className="vsh-title">{title}</span>
        </div>
        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          className="vsh-toggle"
        />
      </div>
      {isOpen && <div className="visit-section-content">{children}</div>}
    </div>
  );
};

function parseTranscript(rawTranscript, agentName = "Kathy") {
  if (!rawTranscript || typeof rawTranscript !== "string") return [];

  const lines = rawTranscript.split("\n").filter((l) => l.trim() !== "");
  const messages = [];

  lines.forEach((line) => {
    const agentMatch = line.match(/^agent:\s*(.*)/i);
    const userMatch = line.match(/^user:\s*(.*)/i);

    if (agentMatch) {
      messages.push({ speaker: agentName, text: agentMatch[1].trim() });
    } else if (userMatch) {
      messages.push({ speaker: "Caller", text: userMatch[1].trim() });
    } else if (messages.length > 0) {
      // continuation line (wrapped text) — append to previous message
      messages[messages.length - 1].text += ` ${line.trim()}`;
    }
  });

  return messages;
}

const AGENT_TRANSCRIPT_ROLE_RE = /\b(agent|ai|assistant|bot|kathy|albert)\b/i;
const CALLER_TRANSCRIPT_ROLE_RE = /\b(caller|user|human|provider|caregiver|client)\b/i;

const UnityCareVisit = ({ clientId }) => {
  const [activeTab, setActiveTab] = useState("agent-latest");
  const [notes, setNotes] = useState([]);
  const [voiceCalls, setVoiceCalls] = useState([]);
  const [regeneratingCallIds, setRegeneratingCallIds] = useState({});
  const [loading, setLoading] = useState(false);
  const regenAttemptedRef = useRef(new Set());

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedArchivedNote, setSelectedArchivedNote] = useState(null);

  // Form State
  const [newNote, setNewNote] = useState({
    date: new Date().toISOString().split("T")[0],
    timeIn: "09:00",
    timeOut: "13:00",
    caregiverName:
      JSON.parse(localStorage.getItem("user"))?.name || "Current User",
    observations: {
      general: "",
      mood: "",
      mobility: "",
      personal: "",
      meals: "",
      activities: "",
      toileting: "",
      meds: "",
      household: "",
      communication: "",
      endShift: "",
      nextPlan: "",
    },
  });

  const [openSections, setOpenSections] = useState({
    general: true,
    mood: false,
    mobility: false,
    personal: false,
    meals: false,
    activities: false,
    toileting: false,
    meds: false,
    household: false,
    communication: false,
    endShift: false,
    nextPlan: false,
  });

  const loadNotes = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [fetchedNotes, fetchedVoiceCalls] = await Promise.all([
        fetchVisitNotes(clientId),
        fetchShiftVoiceCalls(clientId),
      ]);
      setNotes(fetchedNotes);
      setVoiceCalls(Array.isArray(fetchedVoiceCalls) ? fetchedVoiceCalls : []);
    } catch (err) {
      console.error("Failed to load notes", err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      loadNotes();
    }
  }, [clientId, loadNotes]);

  // Auto-refresh notes frequently so newly processed GHL shift calls appear quickly.
  useEffect(() => {
    if (!clientId) return;
    const REFRESH_MS = 60 * 1000;
    const intervalId = setInterval(() => {
      loadNotes();
    }, REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [clientId, loadNotes]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const caregiverId = user?.id || user?._id;

      // Only include caregiverId if it's a valid 24-char hex string
      const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);
      const validCaregiverId = isValidObjectId(caregiverId)
        ? caregiverId
        : null;

      const payload = {
        ...newNote,
        clientId,
        noteType: "care",
        ...(validCaregiverId ? { caregiverId: validCaregiverId } : {}),
      };

      console.log("Creating note with payload:", payload); // Debug

      await createVisitNote(payload);
      setIsCreateModalOpen(false);
      loadNotes(); // Refresh list
      // Reset form slightly but keep date/name
      setNewNote((prev) => ({
        ...prev,
        observations: { ...prev.observations, general: "" },
      }));
    } catch (err) {
      console.error("Error creating note:", err);
      alert(`Failed to create note: ${err.message || "Unknown error"}`);
    }
  };

  const handleObservationChange = (section, value) => {
    setNewNote((prev) => ({
      ...prev,
      observations: {
        ...prev.observations,
        [section]: value,
      },
    }));
  };

  const renderSelect = (
    label,
    section,
    options,
    placeholder = "Select an option",
  ) => (
    <div className="form-group">
      <label>{label}</label>
      <select
        value={newNote.observations[section]}
        onChange={(e) => handleObservationChange(section, e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );

  const resolveNoteType = (note) => note?.noteType || note?.type || "care";
  const formatNoteTime = (note, separator = " - ") => {
    const timeIn = String(note?.timeIn || "").trim();
    const timeOut = String(note?.timeOut || "").trim();
    if (timeIn && timeOut && timeIn === timeOut) return timeIn;
    if (timeIn && timeOut) return `${timeIn}${separator}${timeOut}`;
    return timeIn || timeOut || "N/A";
  };
  const toIsoDateSafe = (value) => {
    const d = new Date(value || "");
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  };
  const toHHMM = (value) => {
    const d = new Date(value || "");
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };
  const formatDurationLabel = (seconds) => {
    const s = Number(seconds || 0);
    if (!Number.isFinite(s) || s <= 0) return "Duration unavailable";
    if (s < 60) return `${s}s`;
    const mins = Math.floor(s / 60);
    const rem = s % 60;
    return rem ? `${mins}m ${rem}s` : `${mins}m`;
  };
  const formatDateYmd = (value) => {
    const d = new Date(value || "");
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleDateString("en-CA").replace(/-/g, "/");
  };
  const formatTranscriptForDisplay = (value) => {
    if (!value) return "";

    let transcriptValue = value;
    if (
      typeof transcriptValue === "string" &&
      transcriptValue.trim().startsWith("[")
    ) {
      try {
        const parsed = JSON.parse(transcriptValue);
        if (Array.isArray(parsed)) {
          transcriptValue = parsed;
        }
      } catch (_e) {
        return transcriptValue;
      }
    }

    let transcript = "";
    if (typeof transcriptValue === "string") {
      transcript = transcriptValue;
    } else if (Array.isArray(transcriptValue)) {
      transcript = transcriptValue
        .map((turn) => {
          if (typeof turn === "string") return turn.trim();
          if (!turn || typeof turn !== "object") return "";

          const role = String(
            turn?.role || turn?.speaker || turn?.name || turn?.source || "Unknown",
          ).trim();
          const message = String(
            turn?.message || turn?.content || turn?.text || turn?.utterance || "",
          ).trim();
          if (!message) return "";
          return `${role}: ${message}`;
        })
        .filter(Boolean)
        .join("\n");
    } else if (typeof transcriptValue === "object") {
      transcript = JSON.stringify(transcriptValue, null, 2);
    } else {
      transcript = String(transcriptValue);
    }

    return transcript
      .replace(/\bbot\s*:/gi, "AI Agent:")
      .replace(/\bhuman\s*:/gi, "Provider:");
  };
  const toTranscriptRoleLabel = (rawRole = "") => {
    const role = String(rawRole || "").trim();
    if (!role) return "Caller";
    if (AGENT_TRANSCRIPT_ROLE_RE.test(role)) return "Kathy";
    if (CALLER_TRANSCRIPT_ROLE_RE.test(role)) return "Caller";
    return role.replace(/[_-]+/g, " ");
  };
  const parseTranscriptRows = (value) => {
    const text = formatTranscriptForDisplay(value);
    if (!text) return [];

    const lines = text
      .split(/\r?\n/)
      .map((line) => String(line || "").trim())
      .filter(Boolean);
    const rows = [];

    lines.forEach((line) => {
      const match = line.match(/^([A-Za-z][A-Za-z0-9_\s-]{0,40})\s*:\s*(.+)$/);
      if (match) {
        const rawRole = String(match[1] || "").trim();
        rows.push({
          role: toTranscriptRoleLabel(rawRole),
          message: String(match[2] || "").trim(),
          isAgent: AGENT_TRANSCRIPT_ROLE_RE.test(rawRole),
        });
        return;
      }

      if (rows.length) {
        rows[rows.length - 1].message = `${rows[rows.length - 1].message} ${line}`.trim();
        return;
      }

      rows.push({
        role: "Transcript",
        message: line,
        isAgent: false,
      });
    });

    return rows.filter((row) => String(row.message || "").trim());
  };
  const hasTranscriptContent = (value) =>
    Boolean(formatTranscriptForDisplay(value).trim());
  const normalizeLocationText = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "Client's home";
    return raw.replace(/^location\s*:\s*/i, "");
  };
  const summaryNeedsRegeneration = (call) => {
    const transcriptText = formatTranscriptForDisplay(call?.transcriptText).trim();
    if (!transcriptText) return false;
    const summaryText = String(call?.summary || "").trim().toLowerCase();
    return (
      !summaryText ||
      summaryText.includes("no transcript available for analysis") ||
      summaryText.includes("summary unavailable") ||
      summaryText.includes("transcript processing")
    );
  };

  useEffect(() => {
    if (!clientId || !Array.isArray(voiceCalls) || voiceCalls.length === 0) return;
    const target = voiceCalls.find(
      (call) =>
        call?._id &&
        summaryNeedsRegeneration(call) &&
        !regenAttemptedRef.current.has(String(call._id)),
    );
    if (!target) return;

    const callId = String(target._id);
    regenAttemptedRef.current.add(callId);
    setRegeneratingCallIds((prev) => ({ ...prev, [callId]: true }));

    regenerateShiftVoiceCallSummary(clientId, callId)
      .then((payload) => {
        const newSummary = String(payload?.summary || "").trim();
        if (!newSummary) return;
        setVoiceCalls((prev) =>
          (Array.isArray(prev) ? prev : []).map((call) =>
            String(call?._id) === callId ? { ...call, summary: newSummary } : call,
          ),
        );
      })
      .catch((err) => {
        console.warn("Failed to regenerate shift summary", err);
      })
      .finally(() => {
        setRegeneratingCallIds((prev) => ({ ...prev, [callId]: false }));
      });
  }, [clientId, voiceCalls]);

  const shouldShowObservation = (key, value) => {
    const text = String(value || "").trim();
    if (!text) return false;
    if ((key === "status" || key === "direction") && text.toLowerCase() === "unknown") {
      return false;
    }
    return true;
  };
  const voiceAsAgentNotes = (voiceCalls || []).map((call) => {
    const ts = toIsoDateSafe(call.callTimestamp || call.createdAt);
    const updatedTs = toIsoDateSafe(call.updatedAt || call.createdAt || call.callTimestamp);
    const duration = Number(call.callDuration || 0);
    const ended = new Date(new Date(ts).getTime() + Math.max(0, duration) * 1000);
    const transcriptText = formatTranscriptForDisplay(call?.transcriptText).trim();
    const summaryText = String(call.summary || call.transcriptSummary || "").trim();
    const hasTranscript = Boolean(transcriptText);
    const isRegenerating = Boolean(regeneratingCallIds[String(call?._id)]);
    const fallbackSummary = hasTranscript
      ? isRegenerating
        ? "Generating AI summary from transcript..."
        : "Transcript captured. Generating AI summary..."
      : "No transcript available for analysis.";
    const callerName =
      call?.rawPayload?.contactName ||
      call?.caregiverName ||
      call?.callerName ||
      "Shift Reporting Agent AI";
    const alertsText = Array.isArray(call.alerts) && call.alerts.length
      ? call.alerts
          .map((a) => `${a?.severity ? `[${a.severity}] ` : ""}${a?.message || ""}`.trim())
          .filter(Boolean)
          .join(" | ")
      : "";
    return {
      _id: `voice-${call._id}`,
      noteType: "agent",
      source: "shift_voice_ai",
      createdAt: call.createdAt || ts,
      updatedAt: updatedTs,
      hasTranscript,
      date: ts,
      timeIn: toHHMM(ts),
      timeOut: toHHMM(ended.toISOString()),
      caregiverName: callerName,
      observations: {
        summary:
          summaryText &&
          !summaryText.toLowerCase().includes("no transcript available for analysis")
            ? summaryText
            : fallbackSummary,
        transcript: transcriptText
          ? formatTranscriptForDisplay(transcriptText)
          : "Transcript processing...",
        status: call.callStatus || "recorded",
        direction: call.callDirection || "",
        callType: call.callType || "",
        duration: formatDurationLabel(duration),
        alerts: alertsText,
      },
    };
  });
  const agentNotes = voiceAsAgentNotes
    .slice()
    .sort((a, b) => {
      const transcriptRank = Number(Boolean(b?.hasTranscript)) - Number(Boolean(a?.hasTranscript));
      if (transcriptRank !== 0) return transcriptRank;
      const da = new Date(a?.updatedAt || a?.createdAt || a?.date || 0).getTime();
      const db = new Date(b?.updatedAt || b?.createdAt || b?.date || 0).getTime();
      return db - da;
    });
  const latestAgentNote = agentNotes.length > 0 ? agentNotes[0] : null;
  const archivedAgentNotes = agentNotes.length > 1 ? agentNotes.slice(1) : [];

  const handleSavePdf = () => {
    // Simple print-to-PDF flow; user can choose "Save as PDF" from the print dialog
    window.print();
  };

  const renderObservationRows = (note) => {
    const entries = Object.entries(note?.observations || {}).filter(
      ([key, value]) => shouldShowObservation(key, value),
    );
    if (!entries.length) {
      return <p style={{ margin: 0, color: "#666" }}>No observations available.</p>;
    }
    return entries.map(([key, value]) => {
      if (key === "transcript") {
        const transcriptRows = parseTranscriptRows(value);
        const transcriptText = formatTranscriptForDisplay(value);
        return (
          <div key={key} style={{ marginBottom: "10px" }}>
            <strong>Transcript:</strong>
            <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
              {transcriptRows.length ? (
                transcriptRows.map((row, idx) => (
                  <TranscriptMessageBubble
                    key={`${row.role}-${idx}`}
                    role={row.role}
                    message={row.message}
                  />
                ))
              ) : (
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#0f172a",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "10px 12px",
                    background: "#f8fafc",
                    fontFamily: "inherit",
                  }}
                >
                  {transcriptText}
                </pre>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={key} style={{ marginBottom: "10px" }}>
          <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>{" "}
          <span style={{ whiteSpace: "pre-line" }}>{String(value)}</span>
        </div>
      );
    });
  };

  return (
    <div className="unity-visit-container">
      {/* Tab Header & Actions */}
      <div
        className="visit-header-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div className="visit-tabs">
          <div
            className={`visit-tab ${
              activeTab === "agent-latest" ? "active" : ""
            }`}
            onClick={() => setActiveTab("agent-latest")}
          >
            <FontAwesomeIcon icon={faBrain} /> Latest Shift Report Note
          </div>
          <div
            className={`visit-tab ${
              activeTab === "agent-archives" ? "active" : ""
            }`}
            onClick={() => setActiveTab("agent-archives")}
          >
            <FontAwesomeIcon icon={faCalendarAlt} /> Archived Shift Report Notes
            ({archivedAgentNotes.length})
          </div>
          <button
            className="visit-tab"
            type="button"
            onClick={loadNotes}
            title="Refresh shift report notes"
            style={{
              marginLeft: 8,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faArrowsRotate} />{" "}
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="action-btn primary-blue"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Create Note
          </button>
          <button className="action-btn primary-blue" onClick={handleSavePdf}>
            Save as PDF report
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="visit-card">
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            Loading visit notes...
          </div>
        ) : activeTab === "agent-latest" ? (
          latestAgentNote ? (
            <>
              <div className="vc-header-blue">
                <div className="vc-title-row">
                  <div className="vc-icon-large">
                    <FontAwesomeIcon icon={faBrain} />
                  </div>
                  <div className="vc-title-text">
                    <h2>Shift Report Note</h2>
                    <div className="vc-meta">
                      <span className="vc-date">
                        <FontAwesomeIcon icon={faCalendarAlt} />{" "}
                        {formatDateYmd(latestAgentNote.date)}
                      </span>
                      <span className="vc-time">
                        <FontAwesomeIcon icon={faClock} />{" "}
                        {formatNoteTime(latestAgentNote, " to ")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="vc-info-row">
                  <div className="vc-info-item">
                    <FontAwesomeIcon icon={faBrain} /> Caller:{" "}
                    {latestAgentNote.caregiverName || "Albert AI"}
                  </div>
                  <div className="vc-info-item">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                    {normalizeLocationText(latestAgentNote.observations?.agency)}
                  </div>
                  <div className="vc-info-item">
                    <FontAwesomeIcon icon={faClock} />{" "}
                    {latestAgentNote.observations?.duration ||
                      "Duration unavailable"}
                  </div>
                </div>
              </div>

              <div className="vc-body">
                {Object.entries(latestAgentNote.observations || {}).map(
                  ([key, value]) => {
                    if (key === "status" || key === "direction") return null;
                    if (!value) return null;
                    return (
                      <CollapsibleSection
                        key={key}
                        title={key.charAt(0).toUpperCase() + key.slice(1)}
                        icon={faBrain}
                        color="purple"
                        isOpen={openSections[key]}
                        onToggle={() => toggleSection(key)}
                      >
                        {key === "summary" ? (
                          <div className="agent-summary-panel">
                            <div className="agent-summary-chip">AI Summary</div>
                            <p className="agent-summary-text" style={{ whiteSpace: "pre-line" }}>
                              {String(value)}
                            </p>
                          </div>
                        ) : key === "transcript" ? (
                          (() => {
                            const transcriptText = formatTranscriptForDisplay(value);
                            const parsedMessages = parseTranscript(transcriptText);
                            return (
                              <div className="space-y-4">
                                {parsedMessages.length > 0 ? (
                                  parsedMessages.map((msg, idx) => (
                                    <TranscriptMessageBubble
                                      key={idx}
                                      role={msg.speaker}
                                      message={msg.text}
                                    />
                                  ))
                                ) : (
                                  // fallback if parsing produces nothing — show raw text so we never
                                  // silently lose data
                                  <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                      {transcriptText}
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <p style={{ whiteSpace: "pre-line" }}>{String(value)}</p>
                        )}
                      </CollapsibleSection>
                    );
                  },
                )}
              </div>
            </>
          ) : (
            <div
              className="empty-state-pane"
              style={{ padding: "40px", textAlign: "center", color: "#666" }}
            >
              <FontAwesomeIcon
                icon={faBrain}
                size="3x"
                style={{ marginBottom: "15px", color: "#ccc" }}
              />
              <h3>No shift report notes found</h3>
              <p>Shift report notes will appear here when available.</p>
            </div>
          )
        ) : (
          <div className="archives-list" style={{ padding: "20px" }}>
            {archivedAgentNotes.length === 0 ? (
              <div className="empty-state">
                No archived shift report notes yet.
              </div>
            ) : (
              archivedAgentNotes.map((note, index) => (
                <div
                  key={note.id || note._id || index}
                  className="archive-item"
                >
                  <div>
                    <strong>{formatDateYmd(note.date)}</strong>
                    <div style={{ fontSize: "13px", color: "#666" }}>
                      {formatNoteTime(note)} •{" "}
                      {note.caregiverName || "Albert AI"} •{" "}
                      {note?.observations?.duration || "Duration unavailable"}
                    </div>
                  </div>
                  <button
                    className="archive-view-btn"
                    onClick={() => setSelectedArchivedNote(note)}
                  >
                    View
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Note Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Visit Note"
      >
        <form onSubmit={handleCreateSubmit} className="create-note-form">
          <div className="form-row two-col">
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={newNote.date}
                onChange={(e) =>
                  setNewNote({ ...newNote, date: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Caregiver Name</label>
              <input
                type="text"
                value={newNote.caregiverName}
                onChange={(e) =>
                  setNewNote({ ...newNote, caregiverName: e.target.value })
                }
                required
              />
            </div>
          </div>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Time In</label>
              <input
                type="time"
                value={newNote.timeIn}
                onChange={(e) =>
                  setNewNote({ ...newNote, timeIn: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Time Out</label>
              <input
                type="time"
                value={newNote.timeOut}
                onChange={(e) =>
                  setNewNote({ ...newNote, timeOut: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div
            className="form-section-title"
            style={{
              marginTop: "20px",
              marginBottom: "10px",
              borderBottom: "1px solid #eee",
            }}
          >
            Observations
          </div>

          <div className="form-group">
            <label>General Observations</label>
            <textarea
              rows="3"
              value={newNote.observations.general}
              onChange={(e) =>
                handleObservationChange("general", e.target.value)
              }
              className="form-textarea"
            ></textarea>
          </div>
          {renderSelect("Mood & Engagement", "mood", [
            "Calm/Cooperative",
            "Anxious/Worried",
            "Irritable/Agitated",
            "Depressed/Withdrawn",
            "Pleasant/Engaged",
          ])}
          {renderSelect("Mobility & Safety", "mobility", [
            "Independent/Steady",
            "Needs Supervision",
            "Uses Device (Cane/Walker)",
            "Unsteady/Fall Risk",
            "Bed/Chair Bound",
          ])}
          {renderSelect("Personal Care", "personal", [
            "Independent",
            "Needs Cueing/Setup",
            "Partial Assist",
            "Full Assist/Dependent",
          ])}
          {renderSelect("Meals & Fluids", "meals", [
            "Ate Well/Drank Well",
            "Ate Lightly/Low Appetite",
            "Minimal Intake",
            "Refused Meals/Fluids",
          ])}
          {renderSelect("Activities & Cognition", "activities", [
            "Engaged/Participated",
            "Needed Prompting/Redirection",
            "Distracted/Low Participation",
            "Confused/Disoriented",
          ])}
          {renderSelect("Toileting", "toileting", [
            "Independent",
            "Needs Standby Assist",
            "Needs Partial Assist",
            "Incontinent/Accidents",
          ])}
          {renderSelect("Medications", "meds", [
            "Taken as Scheduled",
            "Taken with Prompting",
            "Refused/Delayed",
            "Not Applicable",
          ])}
          {renderSelect("Household Tasks", "household", [
            "Completed as Planned",
            "Partially Completed",
            "Declined/Not Completed",
          ])}
          {renderSelect("Communication & Follow-up", "communication", [
            "No New Issues",
            "Left Message/Note for Family",
            "Need Follow-up from Nurse/Provider",
            "Need Family Follow-up",
          ])}
          {renderSelect("End of Shift Status", "endShift", [
            "Stable/No Concerns",
            "Minor Concerns/Monitor",
            "Significant Concern/Notify Provider",
            "Emergency/911",
          ])}
          <div className="form-group">
            <label>Plan for Next Visit</label>
            <textarea
              rows="2"
              value={newNote.observations.nextPlan}
              onChange={(e) =>
                handleObservationChange("nextPlan", e.target.value)
              }
              className="form-textarea"
            ></textarea>
          </div>

          {/* Simplified for MVP - maybe add more later */}

          <div
            className="modal-actions"
            style={{
              marginTop: "20px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button
              type="button"
              className="button cancel-button"
              onClick={() => setIsCreateModalOpen(false)}
            >
              CANCEL
            </button>
            <button type="submit" className="button create-button">
              SAVE NOTE
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(selectedArchivedNote)}
        onClose={() => setSelectedArchivedNote(null)}
        title="Archived Shift Report Note"
      >
        {selectedArchivedNote && (
          <div>
            <div style={{ marginBottom: "12px", color: "#444" }}>
              <div style={{ marginBottom: "6px" }}>
                <strong>Date:</strong> {formatDateYmd(selectedArchivedNote.date)}
              </div>
              <div style={{ marginBottom: "6px" }}>
                <strong>Time:</strong> {formatNoteTime(selectedArchivedNote)}
              </div>
              <div style={{ marginBottom: "6px" }}>
                <strong>Caller:</strong>{" "}
                {selectedArchivedNote.caregiverName || "Albert AI"}
              </div>
              <div style={{ marginBottom: "6px" }}>
                <strong>Location:</strong>{" "}
                {normalizeLocationText(selectedArchivedNote.observations?.agency)}
              </div>
            </div>
            <div style={{ borderTop: "1px solid #eee", paddingTop: "12px" }}>
              {renderObservationRows(selectedArchivedNote)}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UnityCareVisit;
